import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ISubscriptionItem, ISubscriptionPlanCard, SUBSCRIPTION_PLAN } from 'interfaces/subscriptions';

// Updated the type definition to support the object structure for translation keys.
export type TranslationKeyWithParams = string | { key: string; params?: Record<string, any> };

const PROFESSIONAL_TARIFF_COST_MONTHLY_BILL_MONTHLY = 35;
const PROFESSIONAL_TARIFF_COST_MONTHLY_BILL_ANNUAL = 24;

const STARTER_TARIFF_COST_MONTHLY_BILL_MONTHLY = 19;
const STARTER_TARIFF_COST_MONTHLY_BILL_ANNUAL = 13;

const FREE_TARIFF_COST_MONTHLY_BILL_MONTHLY = 0;
const FREE_TARIFF_COST_MONTHLY_BILL_ANNUAL = 0;

export const useSubscriptionsInfo = (): ISubscriptionItem[] => {
  const { t, i18n } = useTranslation();

  return useMemo(() => {
    const FREE_FEATURES_R = [
      t('fullBusinessPlan'),
      t('limitedAiGeneration'),
      t('threeFormats'),
      t('languageGenerations'),
      t('limitedDesignStyle'),
      t('singleProject'),
      t('autoFormattedDesign'),
      t('basedOnBanks'),
      t('waterMark'),
      t('bigFourMethodology'),
      t('fullFinancialPlanning'),
      t('aiFinancialRatiosAnalysis'),
      t('aiPricingSalesGrowth'),
    ];

    const STARTER_FEATURES_R = [
      t('activeProjects', { quantity: 3 }),
      t('fullBusinessPlan'),
      t('threeFormats'),
      t('languageGenerations'),
      t('financialStatements'),
      t('financialRatiosAndForecasts'),
      t('aiGeneratedBankNarrative'),
      t('autoFormattedDesign'),
      t('basedOnBanks'),
      t('unlimitedAiGeneration'),
      t('stepByStepGuidance'),
      t('teamColaboration'),
      t('standardSupport'),
    ].filter(Boolean);

    const PROFESSIONAL_FEATURES_R = [
      t('activeProjects', { quantity: 10 }),
      t('fullBusinessPlan'),
      t('threeFormats'),
      t('languageGenerations'),
      t('financialStatements'),
      t('financialRatiosAndForecasts'),
      t('aiGeneratedBankNarrative'),
      t('autoFormattedDesign'),
      t('basedOnBanks'),
      t('unlimitedAiGeneration'),
      t('stepByStepGuidance'),
      t('teamColaboration'),
      t('prioritySupport'),
    ].filter(Boolean);

    return [
      {
        tariff: SUBSCRIPTION_PLAN.FREE,
        title: t('planCards.free.title'),
        detailsList: FREE_FEATURES_R,
        monthlyPayment: FREE_TARIFF_COST_MONTHLY_BILL_MONTHLY,
        annualPayment: FREE_TARIFF_COST_MONTHLY_BILL_ANNUAL,
      },
      {
        tariff: SUBSCRIPTION_PLAN.LIGHT,
        title: t('planCards.starter.title'),
        detailsList: STARTER_FEATURES_R,
        monthlyPayment: STARTER_TARIFF_COST_MONTHLY_BILL_MONTHLY,
        annualPayment: STARTER_TARIFF_COST_MONTHLY_BILL_ANNUAL,
      },
      {
        tariff: SUBSCRIPTION_PLAN.STANDARD,
        title: t('planCards.professional.title'),
        detailsList: PROFESSIONAL_FEATURES_R,
        monthlyPayment: PROFESSIONAL_TARIFF_COST_MONTHLY_BILL_MONTHLY,
        annualPayment: PROFESSIONAL_TARIFF_COST_MONTHLY_BILL_ANNUAL,
      },
    ];
  }, [i18n.language]);
};

// Hook to provide language-reactive subscription plans data
export const useSubscriptionsPlansData = (): ISubscriptionPlanCard[] => {
  const { t, i18n } = useTranslation();

  return useMemo(() => {
    const FREE_FEATURES_R = [
      t('oneFullBusinessPlan'),
      t('limitedAiGeneration'),
      t('threeFormats'),
      t('languageGenerations'),
      t('limitedDesignStyle'),
      t('singleProject'),
      t('autoFormattedDesign'),
      t('basedOnBanks'),
      t('waterMark'),
      t('bigFourMethodology'),
      t('fullFinancialPlanning'),
      t('aiFinancialRatiosAnalysis'),
      t('aiPricingSalesGrowth'),
    ];

    const STARTER_FEATURES_R = [
      t('activeProjects', { quantity: 3 }),
      t('fullBusinessPlan'),
      t('threeFormats'),
      t('languageGenerations'),
      t('financialStatements'),
      t('financialRatiosAndForecasts'),
      t('aiGeneratedBankNarrative'),
      t('autoFormattedDesign'),
      t('basedOnBanks'),
      t('unlimitedAiGeneration'),
      t('stepByStepGuidance'),
      t('teamColaboration'),
      t('standardSupport'),
    ].filter(Boolean);

    const PROFESSIONAL_FEATURES_R = [
      t('activeProjects', { quantity: 10 }),
      t('fullBusinessPlan'),
      t('threeFormats'),
      t('languageGenerations'),
      t('financialStatements'),
      t('financialRatiosAndForecasts'),
      t('aiGeneratedBankNarrative'),
      t('autoFormattedDesign'),
      t('basedOnBanks'),
      t('unlimitedAiGeneration'),
      t('stepByStepGuidance'),
      t('teamColaboration'),
      t('prioritySupport'),
    ].filter(Boolean);

    return [
      {
        tariff: SUBSCRIPTION_PLAN.FREE,
        tariffTitle: t('planCards.free.title'),
        tariffDescription1: t('planCards.free.description1'),
        tariffDescription2: t('planCards.free.description2'),
        billYearly: {
          tariffCostMonthly: `$${FREE_TARIFF_COST_MONTHLY_BILL_ANNUAL}`,
          tariffCostYearly: '$0',
        },
        billMonthly: {
          tariffCostMonthly: `$${FREE_TARIFF_COST_MONTHLY_BILL_MONTHLY}`,
          tariffCostYearly: '$0',
        },
        tariffFeatures: FREE_FEATURES_R,
      },
      {
        tariff: SUBSCRIPTION_PLAN.LIGHT,
        tariffTitle: t('planCards.starter.title'),
        tariffDescription1: t('planCards.starter.description1'),
        tariffDescription2: t('planCards.starter.description2'),
        billYearly: {
          tariffCostMonthly: `$${STARTER_TARIFF_COST_MONTHLY_BILL_ANNUAL}`,
          tariffCostYearly: '$156',
          tariffCostMonthlyCrossed: '$13',
        },
        billMonthly: {
          tariffCostMonthly: `$${STARTER_TARIFF_COST_MONTHLY_BILL_MONTHLY}`,
          tariffCostYearly: '$228',
        },
        tariffFeatures: STARTER_FEATURES_R,
      },
      {
        tariff: SUBSCRIPTION_PLAN.STANDARD,
        tariffTitle: t('planCards.professional.title'),
        tariffDescription1: t('planCards.professional.description1'),
        tariffDescription2: t('planCards.professional.description2'),
        billYearly: {
          tariffCostMonthly: `$${PROFESSIONAL_TARIFF_COST_MONTHLY_BILL_ANNUAL}`,
          tariffCostYearly: '$288',
          tariffCostMonthlyCrossed: '$24',
        },
        billMonthly: {
          tariffCostMonthly: `$${PROFESSIONAL_TARIFF_COST_MONTHLY_BILL_MONTHLY}`,
          tariffCostYearly: '$420',
        },
        tariffFeatures: PROFESSIONAL_FEATURES_R,
      },
    ];
  }, [i18n.language]);
};
